import { AppNode, DiscreteNode, NodeProperties } from "@features/builder/graph/types"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { getUpdatedNodeProperties } from "@features/builder/NodeDrawer/NodeForm/helpers"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { createNodeLabelMap, createNodeTypeMap, rowsToDfCpd } from "@utils/node"
import { Grid } from "@mui/material"
import { useNodes } from "@xyflow/react"
import { useEffect, useMemo } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { discreteNodeDistributionProperties } from "utils/graph"
import { DistributionChart } from "@components/Distribution/DistributionChart"
import DiscretePropertiesTable from "@features/builder/NodeDrawer/NodeForm/DiscretePropertiesTable"
import { useAtom } from "jotai"
import { selectedPropertiesRowAtom } from "@features/builder/NodeDrawer/NodeForm/propertiesTableAtom"
import { useDfCpdPropertiesValidation } from "@features/builder/NodeDrawer/NodeForm/useDfCpdPropertiesValidation"
import { hasChildNodes } from "@utils/node/getParentsList"

type DiscretePropertiesTableAccordionProps = {
  node: DiscreteNode
  onPropertiesUpdate: (properties: NodeProperties) => void
}

const DiscretePropertiesTableAccordion = (props: DiscretePropertiesTableAccordionProps) => {
  const nodeProperties = props.node.data.originalNode.properties

  const allNodes = useNodes<AppNode>()
  const labelMap = useMemo(() => createNodeLabelMap(allNodes), [allNodes])
  const nodeTypeMap = useMemo(() => createNodeTypeMap(allNodes), [allNodes])
  const form = useFormContext<NodeFormValues>()
  const [selectedRowIndex, setSelectedRowIndex] = useAtom(selectedPropertiesRowAtom)

  const propertiesFormValues = useWatch({ control: form.control, name: "properties" })
  const dfCpd = useMemo(() => rowsToDfCpd(propertiesFormValues), [propertiesFormValues])
  const hasChildren = useMemo(
    () => hasChildNodes(props.node.id, allNodes),
    [allNodes, props.node.id],
  )
  const dfcpdErrors = useDfCpdPropertiesValidation({ dfCpd, hasChildren })

  useEffect(() => {
    setSelectedRowIndex(0)
  }, [props.node.id, setSelectedRowIndex])

  const handleDistributionChange = (newDistribution: string, rowIndex: number) => {
    const formPropertiesValues = form.getValues("properties") ?? []
    if (!formPropertiesValues[rowIndex]) return
    formPropertiesValues[rowIndex].distribution = newDistribution

    const updatedProperties = getUpdatedNodeProperties(
      nodeProperties,
      formPropertiesValues,
      discreteNodeDistributionProperties,
    )
    props.onPropertiesUpdate(updatedProperties)
  }

  return (
    <NodeDrawerAccordion id="properties-table" label="Properties table">
      <Grid item>
        <DiscretePropertiesTable
          nodeLabelMap={labelMap}
          nodeTypeMap={nodeTypeMap}
          nodeProperties={nodeProperties}
          onDistributionChange={handleDistributionChange}
          enableSelection
          validationErrors={dfcpdErrors}
        />
        <DistributionChart dfCpd={dfCpd} rowIndex={selectedRowIndex} />
      </Grid>
    </NodeDrawerAccordion>
  )
}

export default DiscretePropertiesTableAccordion
